import { getCurrentPageKey } from "@/analytics/pageKey";

/**
 * Map the CURRENT page's page_name (getCurrentPageKey()) into the `entry_page` controlled vocab.
 * Use for CTA / element-interaction events, whose entry_page = the page hosting the action (the
 * original intent of the now-removed `entry_page: getCurrentPageKey()` pattern — this fixes only
 * the vocabulary). page_name values with no entry_page equivalent (settings, profile, activity,
 * badges, teams, auth, crop forms, "unknown") collapse to "" (unset).
 *
 * page_name vocab (pageKey.ts) → entry_page vocab (tracking plan):
 *   home                                              → home
 *   search                                            → venue_search_result_list
 *   place | place_space | accessibility_guide | area_map → venue
 *   saved_places | saved_place_list                   → saved_list
 *   notifications                                     → notification
 */
const PAGE_NAME_TO_ENTRY_PAGE: Record<string, string> = {
  home: "home",
  search: "venue_search_result_list",
  place: "venue",
  place_space: "venue",
  accessibility_guide: "venue",
  area_map: "venue",
  saved_places: "saved_list",
  saved_place_list: "saved_list",
  notifications: "notification",
};

export function entryPageFromCurrentPage(
  pageKey: string = getCurrentPageKey(),
): string {
  return PAGE_NAME_TO_ENTRY_PAGE[pageKey] ?? "";
}

/**
 * Resolve the `entry_page` tracking-plan property for page-view events: the page/surface the
 * user came from BEFORE landing here. Classifies `document.referrer` into the plan's controlled
 * vocab (home | venue_search_result_list | map_venue_preview | saved_list | notification | venue
 * | save_venue_form). A same-origin route we don't recognise, an external referrer, or a direct
 * hit (no referrer) yields "" — the "unset" value the plan already ships for home_page_viewed.
 *
 * NOTE: this is the correct source for entry_page, NOT `getCurrentPageKey()` — that returns the
 * CURRENT page's page_name (e.g. "saved_place_list"), which is a different vocabulary and fails
 * the entry_page controlled-vocab check. Mirrors resolveVenueEntryContext in
 * routes/venuedetails.$venueId.tsx (entry_page half only).
 */
export function resolveEntryPage(
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
): string {
  if (!referrer) {
    return "";
  }

  let path = "";
  let sameOrigin = false;
  try {
    const url = new URL(referrer);
    path = url.pathname;
    sameOrigin =
      typeof window !== "undefined" && url.origin === window.location.origin;
  } catch {
    return ""; // malformed referrer — treat as external / unknown
  }

  if (!sameOrigin) {
    return "";
  }

  if (path === "/") {
    return "home";
  }
  if (path.startsWith("/search")) {
    return "venue_search_result_list";
  }
  if (
    path.startsWith("/mysavedplaces") ||
    path.startsWith("/savedplacedetails")
  ) {
    return "saved_list";
  }
  if (path.startsWith("/notifications")) {
    return "notification";
  }
  // Venue-detail family (basic / complex / guide / area-map / photos / reviews).
  if (
    path.startsWith("/venuedetails") ||
    path.startsWith("/complexvenuetabs") ||
    path.startsWith("/accessibility-guide") ||
    path.startsWith("/whats-nearby") ||
    path.startsWith("/venuephotos") ||
    path.startsWith("/venuereviews")
  ) {
    return "venue";
  }

  return "";
}
